const T = 'cycle_counts.events.errors'
// Codes the platform raises; a state conflict means the row on screen is stale, so refetch.
export const KNOWN = {
  COVERAGE_EVENTS_DISABLED: { gateOff: true, refetch: false },
  FULL_WAREHOUSE_EVENT_RESERVED: { gateOff: false, refetch: false },
  COVERAGE_EVENT_NOT_OPEN: { gateOff: false, refetch: true },
  COVERAGE_INCOMPLETE: { gateOff: false, refetch: true },
  LOCATIONS_NOT_ELIGIBLE: { gateOff: false, refetch: true },
  STORE_NOT_FOUND: { gateOff: false, refetch: false },
  // Phase 5 (event-close reconciliation) codes.
  COVERAGE_EVENT_NOT_RECONCILING: { gateOff: false, refetch: true },
  PROPOSAL_NOT_FROZEN: { gateOff: false, refetch: true },
  PROPOSAL_FINGERPRINT_MISMATCH: { gateOff: false, refetch: true },
  // The contract reads it as "not yet signed": a header on screen that says `signed` is stale.
  MERCHANT_SIGNATURE_REQUIRED: { gateOff: false, refetch: true },
  EXECUTION_ALREADY_APPROVED: { gateOff: false, refetch: true },
  EXECUTION_IN_PROGRESS: { gateOff: false, refetch: true },
  MERCHANT_SIGNATURE_REQUIRED_FOR_STORE_WIDE: { gateOff: false, refetch: false },
  // Phase 6 (merchant signature). A state conflict means the header on screen is stale: refetch.
  NOT_AWAITING_SIGNATURE: { gateOff: false, refetch: true },
  SIGNATURE_NOT_REQUIRED: { gateOff: false, refetch: true },
  ALREADY_SIGNED: { gateOff: false, refetch: true },
  // Form refusals of the record-signature dialog: the operator fixes the input, nothing moved.
  EVIDENCE_REQUIRED: { gateOff: false, refetch: false },
  EVIDENCE_TOO_LARGE: { gateOff: false, refetch: false },
  EVIDENCE_TYPE_NOT_ALLOWED: { gateOff: false, refetch: false },
  NOTE_REQUIRED: { gateOff: false, refetch: false },
}

// The server's COVERAGE_INCOMPLETE sentence carries the four state counts inline
// ("Coverage incomplete: N unassigned, N assigned, N in review, N uncovered") — parsed out
// and interpolated into the translated copy instead of showing the raw English sentence.
const COVERAGE_INCOMPLETE_RE = /(\d+) unassigned, (\d+) assigned, (\d+) in review, (\d+) uncovered/

// The copy for a platform code, for a client-side refusal that mirrors one (e.g. a proposal
// without its fingerprint is treated exactly like PROPOSAL_FINGERPRINT_MISMATCH).
export const coverageEventErrorMessage = (code, i18n) => i18n.t(`${T}.${code}`)

// `action` names the mutation the caller was attempting ('close' | 'execute' | …). The WMS
// has no notion of a settler role, so a 401 there gets an extra sentence naming the missing
// authority instead of the generic FORBIDDEN copy. Optional and backward compatible: every
// pre-existing 2-arg call site keeps behaving exactly as before.
export const mapCoverageEventError = (error, i18n, { action } = {}) => {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  const code = data.error_code
  if (code && KNOWN[code]) {
    const rule = KNOWN[code]
    let message
    if (code === 'COVERAGE_INCOMPLETE') {
      const match = typeof data.error === 'string' ? data.error.match(COVERAGE_INCOMPLETE_RE) : null
      // Without a match there are no counts to interpolate — i18n.t against the
      // {{unassigned}}… template with no values would render the literal handlebars,
      // so fall back to a plain sentence that carries no placeholders instead.
      message = match
        ? i18n.t(`${T}.COVERAGE_INCOMPLETE`, { unassigned: match[1], assigned: match[2], in_review: match[3], uncovered: match[4] })
        : i18n.t(`${T}.COVERAGE_INCOMPLETE_GENERIC`)
    } else {
      message = i18n.t(`${T}.${code}`)
    }
    return { code, message, gateOff: rule.gateOff, refetch: rule.refetch }
  }
  if (status === 401) {
    // Cancel of an event that has left `open`, and recording a merchant's signature, are
    // settlement actions on the platform too.
    const code = ['close', 'execute', 'cancel', 'record_signature'].includes(action) ? 'SETTLEMENT_AUTHORITY' : 'FORBIDDEN'
    return { code, message: i18n.t(`${T}.${code}`), gateOff: false, refetch: false }
  }
  if (status === 404) return { code: 'NOT_FOUND', message: i18n.t(`${T}.NOT_FOUND`), gateOff: false, refetch: true }
  if (status === 422 && typeof data.error === 'string') return { code: 'VALIDATION', message: data.error, gateOff: false, refetch: false }
  // An edge or proxy that refuses the multipart body answers 413 with no `error_code`.
  if (status === 413) return { code: 'EVIDENCE_TOO_LARGE', message: i18n.t(`${T}.EVIDENCE_TOO_LARGE`), ...KNOWN.EVIDENCE_TOO_LARGE }
  return { code: 'UNEXPECTED', message: i18n.t(`${T}.UNEXPECTED`), gateOff: false, refetch: false }
}
